import PropTypes from 'prop-types';

const list = (value) => (Array.isArray(value) ? value : []);
const idOf = (value) => String(value?.id ?? value?.imageId ?? '');

const ChangedValue = ({ label, children }) => (
  <div className="ai-record-update-field">
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

const ProjectUpdate = ({ update, project, skillNames }) => {
  const changes = update.changes || {};
  const imageNames = new Map(list(project?.images).map((image, index) => [
    idOf(image),
    image?.title || image?.name || `Image ${index + 1}`,
  ]));

  return (
    <details className="ai-record-update-item">
      <summary>
        <i className="bi bi-kanban" aria-hidden="true"></i>
        <span>{project?.title || changes.title || 'Project'}</span>
        <small>{Object.keys(changes).length} change{Object.keys(changes).length === 1 ? '' : 's'}</small>
      </summary>
      <dl>
        {changes.title ? <ChangedValue label="New title">{changes.title}</ChangedValue> : null}
        {changes.overview ? <ChangedValue label="New description">{changes.overview}</ChangedValue> : null}
        {Array.isArray(changes.skillIds) ? (
          <ChangedValue label="Linked skills">
            {changes.skillIds.length
              ? changes.skillIds.map((id) => skillNames.get(String(id)) || 'Selected skill').join(', ')
              : 'No linked skills'}
          </ChangedValue>
        ) : null}
        {list(changes.imageDescriptions).map((imageChange) => (
          <ChangedValue
            key={imageChange.imageId}
            label={`${imageNames.get(String(imageChange.imageId)) || 'Project image'} description`}
          >
            {imageChange.description || 'Description removed'}
          </ChangedValue>
        ))}
      </dl>
    </details>
  );
};

const SkillUpdate = ({ update, skill }) => {
  const changes = update.changes || {};
  return (
    <details className="ai-record-update-item">
      <summary>
        <i className="bi bi-lightning-charge" aria-hidden="true"></i>
        <span>{skill?.name || changes.title || 'Skill'}</span>
        <small>{Object.keys(changes).length} change{Object.keys(changes).length === 1 ? '' : 's'}</small>
      </summary>
      <dl>
        {changes.title ? <ChangedValue label="New title">{changes.title}</ChangedValue> : null}
        {changes.description ? (
          <ChangedValue label="New description">{changes.description}</ChangedValue>
        ) : null}
        {Object.prototype.hasOwnProperty.call(changes, 'icon') ? (
          <ChangedValue label="New icon">
            {changes.icon ? (
              <span className="ai-record-update-icon">
                <i className={`bi ${changes.icon}`} aria-hidden="true"></i>
                {changes.icon}
              </span>
            ) : 'Icon removed'}
          </ChangedValue>
        ) : null}
      </dl>
    </details>
  );
};

const AiRecordUpdateReview = ({ updates, context }) => {
  const projectUpdates = list(updates?.projectUpdates);
  const skillUpdates = list(updates?.skillUpdates);
  if (!projectUpdates.length && !skillUpdates.length) return null;

  const projects = new Map(list(context?.projects).map((project) => [String(project?.id), project]));
  const skills = new Map(list(context?.skills).map((skill) => [String(skill?.id), skill]));
  const skillNames = new Map(list(context?.skills).map((skill) => [
    String(skill?.id),
    skill?.name || 'Skill',
  ]));

  return (
    <section className="ai-record-update-review" aria-labelledby="ai-record-update-title">
      <header>
        <i className="bi bi-database-check" aria-hidden="true"></i>
        <div>
          <strong id="ai-record-update-title">Saved data changes</strong>
          <small>These changes appear in the portfolio after you confirm them.</small>
        </div>
      </header>

      {projectUpdates.length ? (
        <div className="ai-record-update-group">
          <h4>Projects</h4>
          {projectUpdates.map((update) => (
            <ProjectUpdate
              key={update.id}
              update={update}
              project={projects.get(String(update.id))}
              skillNames={skillNames}
            />
          ))}
        </div>
      ) : null}

      {skillUpdates.length ? (
        <div className="ai-record-update-group">
          <h4>Skills</h4>
          {skillUpdates.map((update) => (
            <SkillUpdate
              key={update.id}
              update={update}
              skill={skills.get(String(update.id))}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
};

ChangedValue.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

ProjectUpdate.propTypes = {
  update: PropTypes.object.isRequired,
  project: PropTypes.object,
  skillNames: PropTypes.instanceOf(Map).isRequired,
};

SkillUpdate.propTypes = {
  update: PropTypes.object.isRequired,
  skill: PropTypes.object,
};

AiRecordUpdateReview.propTypes = {
  updates: PropTypes.object,
  context: PropTypes.object,
};

export default AiRecordUpdateReview;
